import config from 'config';
import { pulseLogo } from 'assets/images';
import { FiAlertTriangle } from 'react-icons/fi';
import React, { useCallback, useState } from 'react';

import { Avatar } from '@vyaguta/vyaguta-ui';
import { UiBuilding } from '@vyaguta/vyaguta-icons/ui';

import PopOver from 'components/common/popover';
import CalendarCross from 'components/common/icons';
import RemoteIcon from 'components/common/remoteIcon';

import { WORK_LOCATION } from 'constants/worklog';
import { mixPanelEvents } from 'constants/mixPanel';
import { EMPLOYEE_PROFILE } from 'constants/routes';
import { attendanceBadgeColorMapper } from 'constants/attendanceStatus';

import * as mixpanel from 'services/mixPanel';

import { buildUrl, getEmpImageUrl, getFirstAndLastName, interpolate } from 'utils/string';

import AttendanceContextPopup from './AttendanceContextPopup';

import type { MyTeamRow, TeamUser } from '../types';

interface AttendanceCellContentProps {
  original: MyTeamRow;
  onViewFullClockInHistory: (user: TeamUser) => void;
  isTeamManager: boolean;
  hasViewClockInHistoryAccess: boolean;
}

const AttendanceCellContent = React.memo(
  ({ original, onViewFullClockInHistory, isTeamManager, hasViewClockInHistoryAccess }: AttendanceCellContentProps) => {
    const [popoverKey, setPopoverKey] = useState(0);

    const handleViewFullClockInHistory = useCallback(
      (user: TeamUser) => {
        setPopoverKey((key) => key + 1);
        onViewFullClockInHistory(user);
      },
      [onViewFullClockInHistory]
    );

    if (!original?.user) {
      return null;
    }

    const { holiday, isWeekend, worklog, leave, checkInTime } = original?.attendance?.today || {};
    const { location } = worklog || {};
    const hasCheckedIn = Boolean(checkInTime);

    const showClockInTimeHistory =
      (Boolean(isTeamManager) && original?.relationType === 'teamManager') || Boolean(hasViewClockInHistoryAccess);

    const getAttendanceBadgeColor = () => {
      if (leave) {
        return attendanceBadgeColorMapper.LEAVE;
      }
      if (!worklog) {
        return attendanceBadgeColorMapper.NO_WORKLOG;
      }
      if (location === WORK_LOCATION.OFFICE && !hasCheckedIn) {
        return attendanceBadgeColorMapper.OFFICE_NOT_CHECKED_IN;
      }
      if (location === WORK_LOCATION.HOME || (location === WORK_LOCATION.OFFICE && hasCheckedIn)) {
        return attendanceBadgeColorMapper.PRESENT;
      }
      return attendanceBadgeColorMapper.NO_WORKLOG;
    };

    const attendanceBadgeColor = getAttendanceBadgeColor();

    const renderAttendanceBadge = () => {
      if (worklog) {
        const Icon = worklog.location === WORK_LOCATION.OFFICE ? UiBuilding : RemoteIcon;

        return (
          <p className="my-team__icon-box">
            <Icon size={12} color="#ffffff" style={{ display: 'block' }} />
          </p>
        );
      }

      if (isWeekend) {
        return <span data-cy="attendance-weekend" />;
      }
      if (holiday) {
        return <span data-cy="attendance-holiday" />;
      }
      if (leave) {
        return (
          <p className="my-team__icon-box">
            <CalendarCross size={12} color="#ab22ff" />
          </p>
        );
      }

      return (
        <p className="my-team__icon-box">
          <FiAlertTriangle size={12} color="#f5af00" style={{ display: 'block' }} />
        </p>
      );
    };

    return (
      <div className="d-flex align-items-center team-member">
        <p className="my-team__row-number">{original.rowNumber}.</p>

        <div className="my-team__avatar">
          <Avatar
            image={getEmpImageUrl(original.user.empId)}
            alt={original.user.fullname}
            className="my-team__avatar-image leapfroggers-profile"
          />

          <PopOver
            key={popoverKey}
            interactive
            interactiveBorder={30}
            trigger="mouseenter"
            position="bottom-start"
            theme={'attendance-popup' as 'light'}
            useContext={false}
            popperOptions={{
              modifiers: {
                flip: {
                  enabled: true,
                  behavior: ['bottom', 'top'],
                },
                preventOverflow: { enabled: false },
              },
            }}
            html={
              <AttendanceContextPopup
                showClockInTimeHistory={showClockInTimeHistory}
                rowData={original}
                onViewFullClockInHistory={handleViewFullClockInHistory}
              />
            }
          >
            <div
              className="my-team__badge my-team__badge--clickable"
              style={{
                backgroundColor: attendanceBadgeColor,
              }}
            >
              <div className="my-team__badge-icon">{renderAttendanceBadge()}</div>
            </div>
          </PopOver>
        </div>

        <div className="team-member__info">
          <a href={interpolate(EMPLOYEE_PROFILE, { id: original.user.id })} target="_blank" rel="noopener noreferrer">
            <p className="team-member__name">{getFirstAndLastName(original.user.fullname)}</p>
          </a>
          <p className="my-team__table-text designation">{original.user.designation?.name}</p>
        </div>

        <a
          className="pulse-icon-container"
          href={interpolate(buildUrl(config.pulseBaseUrl, config.endpoints.pulse.userFeed), { id: original.user.id })}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => mixpanel.trackEvent(mixPanelEvents.select.pulse)}
        >
          <img src={pulseLogo} alt="pulse" />
        </a>
      </div>
    );
  }
);
AttendanceCellContent.displayName = 'AttendanceCellContent';

export default AttendanceCellContent;
